import { OPERATOR_EMPTY_VALUES } from '@/features/metadata-library/config/libraryFormDefaults';
import { LIBRARY_REQUIRED_FIELDS } from '@/features/metadata-library/config/validation';
import type { OperatorFormValues } from '@/features/metadata-library/types/libraryFormValues';
import type { OperatorSnapshot } from '@/features/metadata-library/types/libraryRequests';
import type {
  LibraryFieldsCatalog,
  OperatorRecord,
} from '@/features/metadata-library/types/metadataLibrary';
import { nullableNumber, nullableText } from '@/shared/lib/inputValues';
import { omitNullFields } from '@/shared/lib/omitNullFields';
import { z } from 'zod';
import {
  enumInputSchema,
  handSizeInputSchema,
  integerInputSchema,
  textInputSchema,
} from './fieldSchema';

export function operatorFormDefinition(catalog: LibraryFieldsCatalog) {
  const fields = catalog.fields;
  const requiredFields = LIBRARY_REQUIRED_FIELDS.operator;
  const schema = z
    .object({
      vendor: textInputSchema(fields, 'vendor'),
      vendorOperatorId: textInputSchema(fields, 'vendorOperatorId'),
      operatorName: textInputSchema(fields, 'operatorName', {
        required: requiredFields.operatorName,
      }),
      operatorJob: enumInputSchema(fields, 'operatorJob', { required: requiredFields.operatorJob }),
      ageRange: enumInputSchema(fields, 'ageRange', { required: requiredFields.ageRange }),
      gender: enumInputSchema(fields, 'gender', { required: requiredFields.gender }),
      handedness: enumInputSchema(fields, 'handedness', { required: requiredFields.handedness }),
      consent: enumInputSchema(fields, 'consent'),
      roleExperienceMonths: integerInputSchema(fields, 'roleExperienceMonths'),
      taskExperienceMonths: integerInputSchema(fields, 'taskExperienceMonths'),
      handSizeCm: handSizeInputSchema(fields),
    })
    .strict();
  return { fields, schema };
}

export function operatorValuesFromRecord(record: OperatorRecord): OperatorFormValues {
  return {
    vendor: record.vendor ?? '',
    vendorOperatorId: record.vendorOperatorId ?? '',
    operatorName: record.operatorName ?? '',
    operatorJob: record.operatorJob ?? '',
    ageRange: record.ageRange ?? '',
    gender: record.gender ?? '',
    handedness: record.handedness ?? '',
    consent: record.consent ?? '',
    roleExperienceMonths:
      record.roleExperienceMonths === null ? '' : String(record.roleExperienceMonths),
    taskExperienceMonths:
      record.taskExperienceMonths === null ? '' : String(record.taskExperienceMonths),
    handSizeCm: {
      wristCenterToMiddleFingertipCm: record.handSizeCm
        ? String(record.handSizeCm.wristCenterToMiddleFingertipCm)
        : '',
      thumbToPinkySpreadCm: record.handSizeCm ? String(record.handSizeCm.thumbToPinkySpreadCm) : '',
      middleFingertipToKnuckleCm: record.handSizeCm
        ? String(record.handSizeCm.middleFingertipToKnuckleCm)
        : '',
    },
  };
}

export function operatorPatchBody(values: OperatorFormValues): OperatorSnapshot {
  return {
    vendor: nullableText(values.vendor),
    vendorOperatorId: nullableText(values.vendorOperatorId),
    operatorName: nullableText(values.operatorName),
    operatorJob: nullableText(values.operatorJob),
    ageRange: nullableText(values.ageRange) as OperatorSnapshot['ageRange'],
    gender: nullableText(values.gender) as OperatorSnapshot['gender'],
    handedness: nullableText(values.handedness) as OperatorSnapshot['handedness'],
    consent: nullableText(values.consent) as OperatorSnapshot['consent'],
    roleExperienceMonths: nullableNumber(values.roleExperienceMonths),
    taskExperienceMonths: nullableNumber(values.taskExperienceMonths),
    handSizeCm: Object.values(values.handSizeCm).some((value) => value.trim())
      ? {
          wristCenterToMiddleFingertipCm: Number(values.handSizeCm.wristCenterToMiddleFingertipCm),
          thumbToPinkySpreadCm: Number(values.handSizeCm.thumbToPinkySpreadCm),
          middleFingertipToKnuckleCm: Number(values.handSizeCm.middleFingertipToKnuckleCm),
        }
      : null,
  };
}

export function operatorCreateBody(values: OperatorFormValues): Partial<OperatorSnapshot> {
  return omitNullFields(operatorPatchBody(values));
}

export function operatorCreateValues(
  runnerDisplayName: string,
  vendorOperatorId: string,
): OperatorFormValues {
  return {
    ...OPERATOR_EMPTY_VALUES,
    vendor: runnerDisplayName,
    vendorOperatorId,
    handSizeCm: { ...OPERATOR_EMPTY_VALUES.handSizeCm },
  };
}
